import React, { useState, useEffect, useMemo } from 'react';
import { usePersistedState } from '@/hooks/usePersistedState';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import {
  Calendar, DollarSign, BarChart3, Users, TrendingUp, TrendingDown,
  ArrowUp, ArrowDown, Crown, Filter, Loader2, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ComposedChart, Line, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_DEFS = [
  { key: 'commission',    label: 'Commission',    prefix: 'RM ', color: '#10B981', format: v => `RM ${Math.round(v).toLocaleString()}` },
  { key: 'traded_lot',    label: 'Traded Lot',    color: '#F97316', format: v => v.toFixed(1) },
  { key: 'active_client', label: 'Active Client', color: '#8B5CF6', format: v => v.toLocaleString() },
  { key: 'deposit',       label: 'Deposit',       prefix: 'RM ', color: '#3B82F6', format: v => `RM ${Math.round(v).toLocaleString()}` },
  { key: 'subs',          label: 'Subscribers',   color: '#06B6D4', format: v => v.toLocaleString() },
  { key: 'leads',         label: 'Leads',         color: '#F59E0B', format: v => v.toLocaleString() },
  { key: 'closed',        label: 'Closed',        color: '#22D3EE', format: v => v.toLocaleString() },
  { key: 'com_per_lot',   label: 'Com/Lot',       prefix: '$ ', color: '#EC4899', format: v => `$${v.toFixed(2)}` },
  { key: 'spend',         label: 'Ad Spend',      prefix: 'RM ', color: '#E63946', format: v => `RM ${Math.round(v).toLocaleString()}` },
];

const YEARS = [2026, 2025];

function SectionCard({ children, className = '' }) {
  return <div className={`bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden ${className}`}>{children}</div>;
}

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          {subtitle && <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}

export default function YearlySnapshotPage({ ibIdFilter = null, embedded = false, ibName = null }) {
  const YS_P = `yearly-snapshot:${ibIdFilter || 'all'}`;
  const [year, setYear] = usePersistedState(`${YS_P}:year`, 2026);
  const [data, setData] = useState(null);
  const [targetsData, setTargetsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = usePersistedState(`${YS_P}:metric`, 'commission');
  const [selectedIb, setSelectedIb] = useState(ibIdFilter || 'all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem('token');
    const url = ibIdFilter
      ? `${API}/performance/yearly/${year}?ib_id=${ibIdFilter}`
      : `${API}/performance/yearly/${year}`;
    const tUrl = ibIdFilter
      ? `${API}/performance/yearly-targets/${year}?ib_id=${ibIdFilter}`
      : `${API}/performance/yearly-targets/${year}`;
    Promise.all([
      axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(tUrl, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
    ])
      .then(([res, tRes]) => {
        if (cancelled) return;
        setData(res.data);
        setTargetsData(tRes?.data || null);
      })
      .catch(() => { if (!cancelled) { setData(null); setTargetsData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, ibIdFilter]);

  // Lock IB selection when channel-scoped
  useEffect(() => {
    if (ibIdFilter) setSelectedIb(ibIdFilter);
  }, [ibIdFilter]);

  const metricDef = METRIC_DEFS.find(m => m.key === selectedMetric) || METRIC_DEFS[0];

  // If IB filter active, use that IB's monthly rows, else totals
  const monthlyRows = useMemo(() => {
    if (!data) return [];
    if (selectedIb === 'all') return data.totals_by_month || [];
    const ib = (data.ibs || []).find(x => x.ib_id === selectedIb);
    return ib?.monthly || [];
  }, [data, selectedIb]);

  // Merge targets into monthlyRows for the selected metric
  const targetMonthlyRows = useMemo(() => {
    if (!targetsData || !monthlyRows.length) return monthlyRows;
    let source = null;
    if (selectedIb === 'all') {
      source = targetsData.totals_by_month || [];
    } else {
      const ib = (targetsData.ibs || []).find(x => x.ib_id === selectedIb);
      source = ib?.monthly || [];
    }
    // Always compute the combined "grand total" target across ALL IBs — used
    // as a reference overlay in per-IB view (Item #6).
    const grandByMonth = {};
    (targetsData.totals_by_month || []).forEach(r => { grandByMonth[r.month] = r.targets || {}; });

    if (!source.length && selectedIb === 'all') return monthlyRows;
    const byMonth = {};
    (source || []).forEach(r => { byMonth[r.month] = r.targets || {}; });
    return monthlyRows.map(r => ({
      ...r,
      target: (byMonth[r.month] || {})[selectedMetric] || 0,
      grand_total_target: (grandByMonth[r.month] || {})[selectedMetric] || 0,
    }));
  }, [monthlyRows, targetsData, selectedIb, selectedMetric]);

  // Show target line only if we actually have non-zero targets
  const hasTargetLine = useMemo(
    () => targetMonthlyRows.some(r => (r.target || 0) > 0),
    [targetMonthlyRows]
  );
  // Show "combined total target" line only in per-IB view (avoids duplicate line in All IBs)
  const hasGrandTotalLine = useMemo(
    () => selectedIb !== 'all' && targetMonthlyRows.some(r => (r.grand_total_target || 0) > 0),
    [targetMonthlyRows, selectedIb]
  );

  const bestMonthIdx = useMemo(() => {
    if (!monthlyRows.length) return -1;
    let bestIdx = -1; let bestVal = 0;
    monthlyRows.forEach((r, i) => { if ((r[selectedMetric] || 0) > bestVal) { bestVal = r[selectedMetric]; bestIdx = i; } });
    return bestIdx;
  }, [monthlyRows, selectedMetric]);

  // Heatmap intensity helper
  const maxVal = useMemo(() => Math.max(1, ...monthlyRows.map(r => r[selectedMetric] || 0)), [monthlyRows, selectedMetric]);
  const heatColor = (v) => {
    if (!v || v <= 0) return 'rgba(255,255,255,0.04)';
    const ratio = Math.min(1, v / maxVal);
    if (ratio >= 0.75) return 'rgba(16,185,129,0.55)';  // emerald
    if (ratio >= 0.50) return 'rgba(59,130,246,0.50)';  // blue
    if (ratio >= 0.25) return 'rgba(245,158,11,0.50)';  // amber
    return 'rgba(230,57,70,0.40)';                       // red
  };

  // Per-month growth (vs prev month)
  const monthsWithGrowth = useMemo(() => {
    return monthlyRows.map((m, idx) => {
      const prev = idx > 0 ? monthlyRows[idx - 1] : null;
      const prevVal = prev?.[selectedMetric] || 0;
      const curVal = m[selectedMetric] || 0;
      const growth = prevVal > 0 ? ((curVal - prevVal) / prevVal * 100) : (curVal > 0 ? 100 : 0);
      return { ...m, growth };
    });
  }, [monthlyRows, selectedMetric]);

  const yearTotal = useMemo(() => {
    if (selectedMetric === 'com_per_lot') {
      const totalUSD = monthlyRows.reduce((s, r) => s + (r.commission_usd || 0), 0);
      const totalLot = monthlyRows.reduce((s, r) => s + (r.traded_lot || 0), 0);
      return totalLot > 0 ? totalUSD / totalLot : 0;
    }
    return monthlyRows.reduce((s, r) => s + (r[selectedMetric] || 0), 0);
  }, [monthlyRows, selectedMetric]);
  const monthsWithData = monthlyRows.filter(r => (r[selectedMetric] || 0) > 0).length;
  const avgPerMonth = selectedMetric === 'com_per_lot'
    ? yearTotal  // ratio — same as year avg
    : (monthsWithData > 0 ? yearTotal / monthsWithData : 0);

  const content = (
      <div className="min-h-screen bg-[#09090B]" data-testid="yearly-snapshot">
        {/* Header */}
        <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
          <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Yearly Snapshot {year}{ibName ? ` — ${ibName}` : ''}</h1>
              <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">{ibIdFilter ? 'Channel-scoped 12-month performance' : 'Quick-glance 12-month performance overview'}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="w-[110px] h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                <SelectTrigger className="w-[140px] h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="metric-selector">
                  <Filter size={12} className="mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METRIC_DEFS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {!ibIdFilter && (
              <Select value={selectedIb} onValueChange={setSelectedIb}>
                <SelectTrigger className="w-[160px] h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="ib-selector">
                  <Building2 size={12} className="mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All IBs (Aggregate)</SelectItem>
                  {(data?.ibs || []).map(ib => <SelectItem key={ib.ib_id} value={ib.ib_id}>{ib.name}</SelectItem>)}
                </SelectContent>
              </Select>
              )}
            </div>
          </div>
        </header>

        <div className="px-4 sm:px-8 py-6 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-20 gap-2">
              <Loader2 className="animate-spin text-[#E63946]" size={18} />
              <span className="text-sm text-[hsl(var(--text-muted))]">Loading yearly data…</span>
            </div>
          )}

          {!loading && data && (
            <>
              {/* SUMMARY CARDS */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl p-4 bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="kpi-year-total">
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5">Year {selectedMetric === 'commission' || selectedMetric === 'deposit' || selectedMetric === 'spend' ? 'Total' : 'Sum'}</p>
                  <p className="text-2xl font-bold font-mono" style={{ color: metricDef.color }}>{metricDef.format(yearTotal)}</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{metricDef.label}{selectedIb !== 'all' ? ` • ${(data.ibs||[]).find(x=>x.ib_id===selectedIb)?.name}` : ''}</p>
                </div>
                <div className="rounded-xl p-4 bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5">Avg / Month</p>
                  <p className="text-2xl font-bold font-mono text-white">{metricDef.format(avgPerMonth)}</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{monthsWithData} of 12 months active</p>
                </div>
                <div className="rounded-xl p-4 bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="kpi-best-month">
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5 flex items-center gap-1"><Crown size={11} className="text-[#F59E0B]" /> Best Month</p>
                  <p className="text-2xl font-bold font-mono text-[#F59E0B]">
                    {bestMonthIdx >= 0 ? monthlyRows[bestMonthIdx].label : '—'}
                  </p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
                    {bestMonthIdx >= 0 ? metricDef.format(monthlyRows[bestMonthIdx][selectedMetric]) : 'No data'}
                  </p>
                </div>
                <div className="rounded-xl p-4 bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5">Active IBs</p>
                  <p className="text-2xl font-bold font-mono text-[#10B981]">{(data.ibs || []).length}</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">With data this year</p>
                </div>
              </div>

              {/* MONTHLY GRID */}
              <SectionCard>
                <SectionHeader icon={Calendar} title={`12-Month Grid — ${metricDef.label}`} subtitle="Click month for drill-down (coming soon)" />
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                  {monthsWithGrowth.map((m, idx) => {
                    const isBest = idx === bestMonthIdx;
                    const val = m[selectedMetric] || 0;
                    return (
                      <div
                        key={m.month}
                        className="relative rounded-xl p-3 border transition-all hover:scale-[1.02]"
                        style={{
                          background: heatColor(val),
                          borderColor: isBest ? '#F59E0B' : 'rgba(255,255,255,0.08)',
                          boxShadow: isBest ? '0 0 18px -4px #F59E0B80' : undefined,
                        }}
                        data-testid={`month-card-${m.month}`}
                      >
                        {isBest && (
                          <span className="absolute -top-2 -right-2 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#F59E0B] text-black tracking-wider">Best</span>
                        )}
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-bold text-white tracking-wider">{m.label}</span>
                          <span className="text-[8px] text-white/60">{year}</span>
                        </div>
                        <p className="text-base font-mono font-bold text-white truncate" title={metricDef.format(val)}>
                          {val > 0 ? metricDef.format(val) : <span className="text-white/30">—</span>}
                        </p>
                        {idx > 0 && val > 0 && (
                          <div className={`flex items-center gap-0.5 text-[9px] font-semibold mt-1 ${m.growth >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                            {m.growth >= 0 ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
                            {Math.abs(m.growth).toFixed(1)}% vs {monthlyRows[idx-1]?.label}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>

              {/* BAR CHART with inline metric tabs */}
              <SectionCard>
                <SectionHeader icon={BarChart3} title={`Monthly ${metricDef.label} Chart`} subtitle="Compare across months at a glance" />
                <div className="px-4 pt-3 pb-2 flex items-center gap-1.5 flex-wrap border-b border-[hsl(var(--border))]/50 overflow-x-auto" data-testid="metric-tabs">
                  {METRIC_DEFS.map(md => (
                    <button
                      key={md.key}
                      onClick={() => setSelectedMetric(md.key)}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all whitespace-nowrap border ${
                        selectedMetric === md.key
                          ? 'text-white shadow-lg'
                          : 'text-[hsl(var(--text-muted))] border-[hsl(var(--border))] hover:text-white'
                      }`}
                      style={selectedMetric === md.key
                        ? { background: `${md.color}25`, borderColor: md.color, boxShadow: `0 0 12px -2px ${md.color}40` }
                        : { background: 'transparent' }}
                      data-testid={`metric-tab-${md.key}`}
                    >
                      {md.label}
                    </button>
                  ))}
                </div>
                <div className="p-4" style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={targetMonthlyRows}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} tickFormatter={v => metricDef.prefix ? `${metricDef.prefix}${(v/1000).toFixed(0)}k` : (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0))} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a1a25', border: '1px solid hsl(var(--border))', borderRadius: 8, color: '#fff', fontSize: 12 }}
                        itemStyle={{ color: '#fff' }}
                        labelStyle={{ color: '#fff', fontWeight: 600 }}
                        cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                        formatter={(value, name) => [metricDef.format(value), name === 'target' ? (selectedIb === 'all' ? 'Combined Total Target' : 'IB Target') : (name === 'grand_total_target' ? 'Combined Total Target (All IBs)' : metricDef.label)]}
                      />
                      {(hasTargetLine || hasGrandTotalLine) && <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />}
                      <Bar dataKey={selectedMetric} name={metricDef.label} radius={[6, 6, 0, 0]}>
                        {targetMonthlyRows.map((r, i) => (
                          <Cell key={i} fill={i === bestMonthIdx ? '#F59E0B' : metricDef.color} />
                        ))}
                      </Bar>
                      {hasTargetLine && (
                        <Line
                          type="monotone"
                          dataKey="target"
                          name={selectedIb === 'all' ? 'Combined Total Target' : 'IB Target'}
                          stroke="#F59E0B"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          dot={{ fill: '#F59E0B', r: 3 }}
                          activeDot={{ r: 5 }}
                          isAnimationActive={false}
                        />
                      )}
                      {hasGrandTotalLine && (
                        <Line
                          type="monotone"
                          dataKey="grand_total_target"
                          name="Combined Total Target (All IBs)"
                          stroke="#8B5CF6"
                          strokeWidth={2}
                          strokeDasharray="2 4"
                          dot={{ fill: '#8B5CF6', r: 2 }}
                          activeDot={{ r: 4 }}
                          isAnimationActive={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              {/* PER-IB BREAKDOWN TABLE */}
              {selectedIb === 'all' && (
                <SectionCard>
                  <SectionHeader icon={Users} title="Per-IB Monthly Breakdown" subtitle={`Each cell shows ${metricDef.label} for that month — color intensity = relative magnitude within row`} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))]">
                        <tr>
                          <th className="px-3 py-2 text-left text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider sticky left-0 bg-[hsl(var(--surface-elevated))]">IB</th>
                          {data.totals_by_month.map(m => (
                            <th key={m.month} className="px-2 py-2 text-right text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase">{m.label}</th>
                          ))}
                          <th className="px-3 py-2 text-right text-[10px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.ibs || []).map(ib => {
                          const ibTotal = ib.monthly.reduce((s, r) => s + (r[selectedMetric] || 0), 0);
                          const ibMax = Math.max(1, ...ib.monthly.map(r => r[selectedMetric] || 0));
                          const cellColor = (v) => {
                            if (!v || v <= 0) return 'transparent';
                            const ratio = Math.min(1, v / ibMax);
                            if (ratio >= 0.8) return 'rgba(16,185,129,0.28)';
                            if (ratio >= 0.5) return 'rgba(59,130,246,0.24)';
                            if (ratio >= 0.25) return 'rgba(245,158,11,0.24)';
                            return 'rgba(230,57,70,0.20)';
                          };
                          return (
                            <tr key={ib.ib_id} className="border-b border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))]/40" data-testid={`row-${ib.ib_id}`}>
                              <td className="px-3 py-2 font-semibold text-white sticky left-0 bg-[#09090B]">{ib.name}</td>
                              {ib.monthly.map(m => {
                                const v = m[selectedMetric] || 0;
                                return (
                                  <td key={m.month} className="px-2 py-2 text-right font-mono" style={{ backgroundColor: cellColor(v) }}>
                                    {v > 0 ? (
                                      metricDef.prefix === 'RM '
                                        ? `${Math.round(v).toLocaleString()}`
                                        : (typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(1) : v.toLocaleString())
                                    ) : <span className="text-white/20">·</span>}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: metricDef.color }}>{metricDef.format(ibTotal)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </div>
      </div>
  );

  return embedded ? content : <ResponsiveLayout>{content}</ResponsiveLayout>;
}
